import React, { useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  activeChatbotSelector,
  chatbotIsActiveOnConnectedPageSelector,
} from '@mm/state/selectors/viewSelectors';

import { Circle, Heading, Line } from '@mm/components';
import { ArrowStatefulIcon } from '@mm/icons';
import { useClickOutside } from '@mm/hooks';

import { colors } from '../../../../editor/features/beta/styles/colors';

import css from './ChatbotDropdown.module.css';
import { ToggleBotLive } from '../../../../common/components/ToggleBotLive/ToggleBotLive';
import { ChatbotChannelsToggle } from '../../../../editor/features/configure/panels/sections/BotSettingsSection/ChatbotStatusSection/ChatbotStatusSection';
import Spinner from '../../../../common/components/Spinner';
import { UserRoleContainer } from '../../../../common/hooks/use-route-guard';
import { BotBuilderRoutes } from '../../../../routes/bot-builder-routes';

type Props = {
  className?: string;
};

export const ChatbotDropdown = ({ className }: Props) => {
  const history = useHistory();
  const chatbot = useSelector(activeChatbotSelector);
  const isActive = useSelector(chatbotIsActiveOnConnectedPageSelector);

  const [isOpen, setIsOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSuccess = async () => {
    setBusy(false);
  };

  const onReject = () => {
    setBusy(false);
  };

  const onToggle = () => {
    setBusy(true);
  };

  const onClick = () => {
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const onOutsideClick = () => {
    if (isOpen) {
      setIsOpen(false);
    }
  };

  const ref = useClickOutside<HTMLDivElement>(onOutsideClick);

  const {
    roleFlags: { isLimitedRole },
  } = UserRoleContainer.useContainer();

  const onSwitchClick = () => {
    if (chatbot.id && !isLimitedRole) {
      const path = BotBuilderRoutes.settingsChatbot.getLink({
        chatbotId: chatbot.id,
      });
      history.push(path);
      setIsOpen(false);
    }
  };

  if (!chatbot) {
    return <></>;
  }

  return (
    <div
      className={classNames(className, css.container, {
        [css.open]: isOpen,
        [css.inactive]: !isActive,
      })}
      onClick={onClick}
    >
      <div className={css.info}>
        Bot:&nbsp;<span className={css.name}>{chatbot.name}</span>
      </div>

      <div className={css.status}>
        <Circle
          className={css.icon}
          color={isActive ? colors.green : colors.red}
        />
        <span>{isActive ? 'Active.' : 'Inactive'}</span>
        &nbsp;
        <span className={css.change}>
          {isActive ? 'Change.' : 'Activate this bot'}
        </span>
      </div>

      {isOpen && (
        <div
          className={classNames(css.dropdown, { [css.busy]: busy })}
          ref={ref}
        >
          <div className={css.info}>
            <Heading size={5} element="span">
              Bot:
            </Heading>
            &nbsp;
            <Heading size={5} element="span" className={css.name}>
              {chatbot.name}
            </Heading>
            <ToggleBotLive
              onSuccess={onSuccess}
              onReject={onReject}
              onToggle={onToggle}
              className={css.toggle}
              bot={chatbot}
            />
          </div>

          <Line />

          {busy && <Spinner className={css.loader} standalone />}

          {!busy && (
            <ChatbotChannelsToggle
              css={{ container: css.channels, channel: css.channel }}
              onSwitch={onToggle}
              onSuccess={onSuccess}
            />
          )}

          <Line />

          <div className={css.switch} onClick={onSwitchClick}>
            <Heading
              className={classNames({
                [css.disabled]: isLimitedRole,
              })}
              size={5}
            >
              Switch to another bot
            </Heading>
            <ArrowStatefulIcon
              direction="right"
              color={isLimitedRole ? colors.gray : colors.accentBlue}
            />
          </div>
        </div>
      )}
    </div>
  );
};
